import React, { useEffect, useState } from "react";
import {
  FileSignature, Plus, X, Trash2, ExternalLink, RefreshCw, Loader2, Globe, Link2, ArrowLeft,
} from "lucide-react";
import { toast } from "sonner";
import Layout, { PageHeader } from "@/components/Layout";
import api from "@/lib/api";
import { useAuth, ROLE_LABELS } from "@/lib/auth.jsx";
import { getNav } from "@/pages/navConfigs";
import { DocThumb, NewDocThumb } from "@/components/DocThumb";

/**
 * Documents page (Google Docs first).
 *
 * - No in-app editor. Users create their docs in Google Docs natively.
 * - Users register a doc by pasting its Google Docs URL → we extract the doc
 *   id, try to pull title + thumbnail via Drive, and persist a card.
 * - Clicking a card opens an in-dashboard preview (embedded /preview iframe)
 *   plus an "Edit in Google Docs" button that opens the real editor in a new
 *   tab. When the user comes back the preview iframe re-loads → live content.
 */
export default function DocumentsPage({
  kind = "doc",
  pageTitle = "Documents",
  pageSubtitle = "Add Google Docs you’ve created and keep them at your fingertips.",
}) {
  const { user, customRole } = useAuth();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [current, setCurrent] = useState(null);   // doc being previewed
  const [addOpen, setAddOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [gwsStatus, setGwsStatus] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/documents?kind=${kind}`);
      setDocs(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Could not load documents");
    } finally {
      setLoading(false);
    }
  };

  const loadStatus = async () => {
    try {
      const r = await api.get("/google/status");
      setGwsStatus(r.data);
    } catch { /* ignore */ }
  };

  useEffect(() => { load(); loadStatus(); }, [kind]); // eslint-disable-line react-hooks/exhaustive-deps

  const removeDoc = async (d) => {
    if (!window.confirm(`Remove “${d.title || "Untitled"}” from the dashboard?\n\nThe original Google Doc is NOT deleted — it stays in your Drive.`)) return;
    try {
      await api.delete(`/documents/${d.doc_id}`);
      setDocs((prev) => prev.filter((x) => x.doc_id !== d.doc_id));
      if (current?.doc_id === d.doc_id) setCurrent(null);
      toast.success("Removed from dashboard");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Delete failed");
    }
  };

  const refreshMeta = async (d) => {
    setBusy(true);
    try {
      const r = await api.post(`/documents/${d.doc_id}/refresh`);
      if (r.data?.updated) {
        toast.success("Refreshed from Google");
        await load();
      } else {
        toast.error(r.data?.reason || "No metadata available — make sure the doc is shared with the connected Google account.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Refresh failed");
    } finally { setBusy(false); }
  };

  const previewSrc = current?.gdoc_id
    ? `https://docs.google.com/document/d/${current.gdoc_id}/preview?rm=minimal`
    : null;

  return (
    <Layout navItems={getNav(user?.role, customRole)} title={pageTitle}>
      <div className="p-6 lg:p-10 max-w-7xl mx-auto">
      <PageHeader
        title={pageTitle}
        subtitle={pageSubtitle}
        chip={ROLE_LABELS[user?.role]}
        actions={
          <button
            onClick={() => setAddOpen(true)}
            className="btn-primary py-2.5 px-5 flex items-center gap-2"
            data-testid="doc-add-btn"
          >
            <Plus className="w-4 h-4" /> Add Google Doc
          </button>
        }
      />

      {/* Active doc preview panel */}
      {current && (
        <div className="dashboard-card overflow-hidden mb-6" data-testid="doc-preview-panel">
          <div className="flex items-center justify-between gap-3 px-5 py-3 border-b bg-white">
            <div className="flex items-center gap-3 min-w-0">
              <button onClick={() => setCurrent(null)} className="p-2 rounded-full hover:bg-slate-100" title="Back to list" data-testid="doc-preview-close">
                <ArrowLeft className="w-4 h-4" />
              </button>
              <div className="min-w-0">
                <div className="font-display font-bold text-base truncate">{current.title || "Untitled"}</div>
                <div className="text-xs text-slate-500 truncate">
                  Google Doc · {current.gdoc_modified_at ? `last modified ${new Date(current.gdoc_modified_at).toLocaleString()}` : "preview only"}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={() => refreshMeta(current)}
                disabled={busy}
                className="btn-ghost text-xs px-3 py-2 flex items-center gap-1.5 disabled:opacity-50"
                title="Fetch the latest title / thumbnail from Drive"
                data-testid="doc-preview-refresh"
              >
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Refresh
              </button>
              <a
                href={current.gdoc_web_url}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary text-sm px-4 py-2 flex items-center gap-1.5"
                data-testid="doc-preview-edit"
              >
                <ExternalLink className="w-4 h-4" /> Edit in Google Docs
              </a>
            </div>
          </div>

          {previewSrc ? (
            <iframe
              key={current.doc_id}
              src={previewSrc}
              title={current.title || "Document preview"}
              className="w-full bg-slate-50"
              style={{ height: "80vh", border: 0 }}
              data-testid="doc-preview-iframe"
              loading="lazy"
            />
          ) : (
            <div className="p-10 text-center text-slate-500 text-sm">Preview unavailable.</div>
          )}

          <div className="px-5 py-3 border-t bg-slate-50 text-xs text-slate-500 flex items-center gap-2">
            <Globe className="w-3.5 h-3.5" />
            Live preview from Google Docs. Edits made in Google save automatically and appear here on the next refresh.
          </div>
        </div>
      )}

      {/* Doc grid */}
      {!current && (
        <>
          {loading ? (
            <div className="flex items-center gap-2 text-slate-500 p-12 justify-center">
              <Loader2 className="w-5 h-5 animate-spin" /> Loading documents…
            </div>
          ) : docs.length === 0 ? (
            <div className="dashboard-card p-10 text-center" data-testid="doc-empty-state">
              <FileSignature className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <div className="font-display font-bold text-lg">No documents yet</div>
              <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">
                Create your document in Google Docs from your own Google account, then add its link here. The doc stays in your Drive — we just keep a thumbnail and a quick-open card in the dashboard.
              </p>
              <button onClick={() => setAddOpen(true)} className="btn-primary mt-5 py-2.5 px-5 inline-flex items-center gap-2">
                <Plus className="w-4 h-4" /> Add your first Google Doc
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              <NewDocThumb onClick={() => setAddOpen(true)} />
              {docs.map((d) => (
                <DocThumb
                  key={d.doc_id}
                  doc={d}
                  active={current?.doc_id === d.doc_id}
                  canDelete={d.owner_id === user?.user_id}
                  onOpen={() => setCurrent(d)}
                  onDelete={() => removeDoc(d)}
                />
              ))}
            </div>
          )}
        </>
      )}

      {addOpen && (
        <AddGoogleDocModal
          gwsConnected={!!gwsStatus?.connected}
          gwsEmail={gwsStatus?.email}
          onClose={() => setAddOpen(false)}
          onAdded={async (d) => { setAddOpen(false); await load(); setCurrent(d); }}
          kind={kind}
        />
      )}
      </div>
    </Layout>
  );
}

function AddGoogleDocModal({ gwsConnected, gwsEmail, onClose, onAdded, kind }) {
  const [url, setUrl] = useState("");
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const validId = /\/d\/([a-zA-Z0-9_-]{16,})/.exec(url || "")?.[1];

  const submit = async () => {
    if (!validId) {
      toast.error("Paste the full Google Docs URL (https://docs.google.com/document/d/…)");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/documents/link", { gdoc_url: url, title, kind });
      toast.success("Added — Google Doc is now in your dashboard");
      onAdded(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Could not add doc");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-lg w-full p-6 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="add-doc-modal">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-sky-100 text-sky-700 flex items-center justify-center"><Link2 className="w-5 h-5" /></div>
            <div>
              <h3 className="font-display font-bold text-lg">Add a Google Doc</h3>
              <p className="text-xs text-slate-500">Paste the share link of any Google Doc you’ve created.</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1 rounded-full hover:bg-slate-100" data-testid="add-doc-close"><X className="w-5 h-5" /></button>
        </div>

        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold text-slate-700">Google Doc URL</label>
            <input
              autoFocus
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://docs.google.com/document/d/…"
              className="input-pill mt-1 font-mono text-xs"
              data-testid="add-doc-url"
            />
            {url && !validId && (
              <p className="text-[11px] text-rose-600 mt-1">That doesn’t look like a Google Doc URL — copy it from the browser address bar.</p>
            )}
            {validId && (
              <p className="text-[11px] text-emerald-700 mt-1">✓ Doc ID detected: <code className="bg-emerald-50 px-1 rounded">{validId.slice(0, 12)}…</code></p>
            )}
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-700">Card title (optional)</label>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Leave blank to auto-fetch from Google"
              className="input-pill mt-1"
              data-testid="add-doc-title"
            />
          </div>

          {gwsConnected ? (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-[11px] text-amber-900">
              <b>Heads-up:</b> for the dashboard to fetch the live title &amp; thumbnail, the doc must be shared (View access is enough) with <code className="bg-white px-1 rounded">{gwsEmail || "the connected Google account"}</code>. Otherwise we’ll save it with your manual title and the preview will still work as long as you’re signed in to Google in your browser.
            </div>
          ) : (
            <div className="bg-sky-50 border border-sky-200 rounded-lg p-3 text-[11px] text-sky-900">
              Tip: connect Google Workspace in Admin → System to auto-fetch titles &amp; thumbnails. For now the preview will still open if you’re signed in to Google in this browser.
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 mt-5 pt-4 border-t">
          <button onClick={onClose} className="btn-ghost px-4 py-2" data-testid="add-doc-cancel">Cancel</button>
          <button onClick={submit} disabled={busy || !validId} className="btn-primary px-5 py-2 flex items-center gap-2 disabled:opacity-50" data-testid="add-doc-submit">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            {busy ? "Adding…" : "Add to dashboard"}
          </button>
        </div>
      </div>
    </div>
  );
}
